import React, { useState, useEffect, useRef } from 'react';
import { X, Search } from 'lucide-react';

const API_BASE_URL = '/api';
const ASSETS_URL = '';

// Abkhaz alphabet letters for navigation
const ALPHABET_LETTERS = [
  'А', 'Б', 'В', 'Г', 'Гь', 'Гә', 'Ӷ', 'Ӷь', 'Ӷә', 
  'Д', 'Дә', 'Е', 'Ж', 'Жь', 'Жә', 'З', 'Ӡ', 'Ӡә', 
  'И', 'К', 'Кь', 'Кә', 'Қ', 'Қь', 'Қә', 'Ҟ', 'Ҟь', 'Ҟә',
  'Л', 'М', 'Н', 'О', 'П', 'Ԥ', 'Р', 'С', 'Т', 'Тә',
  'Ҭ', 'Ҭә', 'У', 'Ф', 'Х', 'Хь', 'Хә', 'Ҳ', 'Ҳә',
  'Ц', 'Цә', 'Ҵ', 'Ҵә', 'Ч', 'Ҷ', 'Ҽ', 'Ҿ',
  'Ш', 'Шь', 'Шә', 'Ы', 'Ҩ', 'Џ', 'Џь'
];

const Dictionary = ({ isOpen, onClose }) => {
  const [dictionaryData, setDictionaryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeLetter, setActiveLetter] = useState(null);
  const contentRef = useRef(null);
  const letterRefs = useRef({});

  useEffect(() => {
    if (isOpen) {
      fetchDictionary();
    }
  }, [isOpen]);

  const fetchDictionary = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/dictionary`);
      if (response.ok) {
        const data = await response.json();
        setDictionaryData(data);
      }
    } catch (error) {
      console.error('Failed to fetch dictionary:', error);
    } finally {
      setLoading(false);
    }
  };

  const scrollToLetter = (letter) => {
    setActiveLetter(letter);
    const element = letterRefs.current[letter];
    if (element && contentRef.current) {
      const containerTop = contentRef.current.getBoundingClientRect().top;
      const elementTop = element.getBoundingClientRect().top;
      const offset = elementTop - containerTop;
      contentRef.current.scrollTop += offset - 10;
    }
  };

  const filteredData = searchTerm
    ? dictionaryData.filter(section => 
        section.entries.some(entry => 
          entry.word.toLowerCase().includes(searchTerm.toLowerCase()) ||
          entry.translation.toLowerCase().includes(searchTerm.toLowerCase())
        )
      ).map(section => ({
        ...section,
        entries: section.entries.filter(entry =>
          entry.word.toLowerCase().includes(searchTerm.toLowerCase()) ||
          entry.translation.toLowerCase().includes(searchTerm.toLowerCase())
        )
      }))
    : dictionaryData;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <img 
              src={`${ASSETS_URL}/assets/images/dic_icon.png`}
              alt="ლექსიკონი"
              className="w-8 h-8"
            />
            <h2 className="text-xl font-bold text-gray-900 font-['bpg-caps']">
              ლექსიკონი
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close dictionary"
          >
            <X size={24} className="text-gray-500" />
          </button>
        </div>

        {/* Search Bar */}
        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="ძიება..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Alphabet Navigation */}
        <div className="p-3 border-b border-gray-200 bg-gray-50">
          <div className="flex flex-wrap gap-1 justify-center">
            {ALPHABET_LETTERS.map((letter) => (
              <button
                key={letter}
                onClick={() => scrollToLetter(letter)}
                className={`px-2 py-1 text-sm font-medium rounded transition-colors min-w-[32px]
                  ${activeLetter === letter 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-white text-gray-700 hover:bg-blue-100 hover:text-blue-600 border border-gray-200'
                  }
                  ${!dictionaryData.some(s => s.letter === letter) ? 'opacity-50 cursor-not-allowed' : ''}
                `}
                disabled={!dictionaryData.some(s => s.letter === letter)}
              >
                {letter}
              </button>
            ))}
          </div>
        </div>

        {/* Dictionary Content */}
        <div 
          ref={contentRef}
          className="flex-1 overflow-y-auto p-4"
        >
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-gray-500 font-['bpg-caps']">იტვირთება...</div>
            </div>
          ) : filteredData.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-gray-500 font-['bpg-caps']">
                {searchTerm ? 'შედეგები არ მოიძებნა' : 'ლექსიკონი ცარიელია'}
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {filteredData.map((section) => (
                <div 
                  key={section.letter}
                  ref={(el) => letterRefs.current[section.letter] = el}
                >
                  {/* Letter Header */}
                  <div className="sticky top-0 bg-blue-600 text-white px-4 py-2 rounded-lg mb-3 shadow-md">
                    <h3 className="text-lg font-bold">{section.letter}</h3>
                  </div>
                  
                  {/* Entries */}
                  <div className="space-y-2 pl-2">
                    {section.entries.map((entry, index) => (
                      <div 
                        key={index}
                        className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-3">
                          <span className="font-semibold text-gray-900 min-w-0 break-words">
                            {entry.word}
                          </span>
                          {entry.grammar && (
                            <span className="text-sm text-blue-600 italic">
                              ({entry.grammar})
                            </span>
                          )}
                        </div>
                        <div className="text-gray-700 mt-1">
                          {entry.translation}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Dictionary Button Component to be used in headers
export const DictionaryButton = ({ onClick }) => {
  return (
    <button
      onClick={onClick}
      className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
      aria-label="Open dictionary"
      title="ლექსიკონი"
    >
      <img 
        src={`${ASSETS_URL}/assets/images/dic_icon.png`}
        alt="ლექსიკონი"
        className="w-6 h-6"
      />
    </button>
  );
};

export default Dictionary;
